/* eslint-disable react-hooks/rules-of-hooks */
import { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { Card } from '@components/card/card';
import { CardBody, Form, Badge, Row, Col, InputGroup } from 'react-bootstrap';
import Breadcrumb from '@components/Shared/Breadcrumb';
import Loading from '@components/Shared/Loader/Loading';
import CustomAlert from '@components/Shared/Alert/CustomAlert';
import confirmAction from '@components/Shared/Alert/confirmAction';
import useLookupCurrency from '@hooks/Lookups/use-Lookup-Currency';
import useGetOperatingUnitWithPermission from '@hooks/NTX/Organization/useGetOperatingUnitWithPermission';
import useGetPersonSuppliersList from '@hooks/NTX/Peoples/useGetPersonSuppliersList';
import useGetApExpenseCategoryList from '@hooks/Finance/Expense/useGetApExpenseCategoryList';
import { useAppDispatch } from '@store/hooks';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AddApExpanseSchema } from '../../../ValidationForm/validationSchema';
import { addApExpense, updateApExpense, getApExpenseById } from '@store/Finance/Expense/ApExpense/ApExpenseSlice';
import ApExpenseLines from './ApExpenseLines';
import type { ApExpenseModels } from '@store/Finance/Expense/ApExpense/ApExpenseModels';
import { v4 as uuidv4 } from 'uuid';
import useGetPaymentMethod from '@hooks/Payrolls/use-Get-PaymentMethod';
import useGetBankAccountsList from '@hooks/Finance/BankAccounts/useGetBankAccountsList';
import ButtonPrint from '@components/Print/ButtonPrint';
import { useDailyRate } from '@hooks/Finance/DailyRates/useDailyRate';

interface ApExpenseFormProps {
  isEdit?: boolean;
  isViewOnly?: boolean;
  id?: string;
}

// Helpers
const toStr = (v: any) => (v === null || v === undefined ? '' : String(v));
const isEmpty = (v: any) => v === null || v === undefined || v === '';

const makeEmptyLine = () => ({
  rowKey: uuidv4(),
  id: '',
  expenseCategoryItemId: '',
  description: '',
  amount: 0,
  vat: 0,
  isIncludeVat: true,
  currencyCode: '',
  currencyConversionRate: 1,
  existingAttachment: null,
  fileAttachment: null as File | null
});

const ApExpenseForm = ({ isEdit = false, isViewOnly = false, id }: ApExpenseFormProps) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const paramsId = useParams().id;
  const expenseId = isViewOnly ? id : paramsId;
  const [searchParams] = useSearchParams();

  // الحالة
  const [isSubmitting, setisSubmitting] = useState(false);
  const [loadingData, setLoadingData] = useState(isEdit || isViewOnly);
  const [dataById, setDataById] = useState<ApExpenseModels | undefined>(undefined);

  // البيانات المرجعية (Lookups)
  const { Currency, loading: loading4 } = useLookupCurrency();
  const { OperatingUnitOptions, loading: loading7 } = useGetOperatingUnitWithPermission();
  const { OptionsExpenseCategory } = useGetApExpenseCategoryList();
  const { OptionPersonSuppliers, loading: loading2 } = useGetPersonSuppliersList();
  const { PaymentMethodList } = useGetPaymentMethod();
  const { OptionsBankAccounts, groupedOptions } = useGetBankAccountsList();

  const todayDate = useMemo(() => new Date().toISOString().split('T')[0], []);
  const defaultExpenseType = useMemo<'FAST' | 'NORMAL'>(() => {
    const q = (searchParams.get('type') || 'NORMAL').toUpperCase();
    return q === 'FAST' ? 'FAST' : 'NORMAL';
  }, [searchParams]);

  // Fetch Data
  useEffect(() => {
    if ((isEdit || isViewOnly) && expenseId) {
      const fetchData = async () => {
        try {
          const res = await dispatch(getApExpenseById(expenseId)).unwrap();
          if (res?.succeeded) setDataById(res.data as ApExpenseModels);
        } catch (error) {
          console.error('Fetch error:', error);
        } finally {
          setLoadingData(false);
        }
      };
      fetchData();
    } else {
      setDataById(undefined);
      setLoadingData(false);
    }
  }, [isEdit, isViewOnly, expenseId, dispatch]);

  const initialValues = useMemo(() => {
    if ((isEdit || isViewOnly) && dataById) {
      return {
        ...dataById,
        expenseDate: dataById.expenseDate ? dataById.expenseDate.split('T')[0] : todayDate,
        // ExpensType: dataById.expensType ?? '',
        apExpenseLinesList:
          Array.isArray(dataById.apExpenseLinesList) && dataById.apExpenseLinesList.length > 0
            ? dataById.apExpenseLinesList.map((item: any) => ({
                ...item,
                rowKey: item.id ?? uuidv4(),
                existingAttachment: Array.isArray(item.attachment) ? item.attachment[0] : null,
                fileAttachment: null
              }))
            : [makeEmptyLine()]
      };
    }
    return {
      id: '',
      no: 0,
      expenseCategoryId: '',
      organizationId: '',
      peopleId: '',
      personId: '',
      expenseDate: todayDate,
      description: '',
      totalAmount: 0,
      totalVat: 0,
      isIncludeVat: false,
      ameStatus: 0,
      isAme: false,
      currencyCode: '',
      currencyConversionRate: 1,
      isConfirmed: false,
      expensType: defaultExpenseType,
      paymentMethodId: null,
      apBankAccountId: null,
      apExpenseLinesList: [makeEmptyLine()]
    };
  }, [isEdit, isViewOnly, dataById, todayDate, defaultExpenseType]);

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: AddApExpanseSchema(t),
    onSubmit: async values => {
      const isConfirmation = await confirmAction(t('هل ترغب بالتأكيد على حفظ هذه البيانات؟'));
      if (!isConfirmation) return;

      setisSubmitting(true);
      const lines = values.apExpenseLinesList || [];
      const totalAmount = lines.reduce((sum: number, item: any) => sum + (Number(item.amount) || 0), 0);
      const totalVat = lines.reduce((sum: number, item: any) => sum + (Number(item.vat) || 0), 0);

      // بناء FormData
      const fd = new FormData();
      Object.entries(values).forEach(([key, value]) => {
        if (key !== 'apExpenseLinesList') fd.append(key, toStr(value));
      });
      fd.set('totalAmount', toStr(totalAmount));
      fd.set('totalVat', toStr(totalVat));

      lines.forEach((line: any, i: number) => {
        Object.entries(line).forEach(([lKey, lVal]) => {
          if (lKey !== 'fileAttachment' && lKey !== 'rowKey') {
            fd.append(`apExpenseLinesList[${i}].${lKey}`, toStr(lVal));
          }
        });
        if (line.fileAttachment instanceof File) {
          fd.append(`apExpenseLinesList[${i}].fileAttachment`, line.fileAttachment);
        }
      });

      const action = isEdit ? updateApExpense(fd) : addApExpense(fd);

      try {
        const response = await dispatch(action).unwrap();

        if (response.succeeded === true) {
          CustomAlert({ action: isEdit ? 'Edit' : 'Add' });
          navigate('/AP/ApExpense', { replace: true });
        } else {
          CustomAlert({ action: 'Error', msg: response?.message });
        }
      } catch (err: any) {
        console.error(err);

        CustomAlert({ action: 'Error', msg: err.message });
      } finally {
        setisSubmitting(false);
      }

      // ;

      // dispatch(action)
      //   .unwrap()
      //   .then((res: any) => {
      //     if (res?.succeeded) {
      //       CustomAlert({ action: isEdit ? 'Edit' : 'Add' });
      //       navigate('/AP/ApExpense', { replace: true });
      //     } else {
      //       CustomAlert({ action: 'Error', msg: res?.message });
      //     }
      //   })
      //   .finally(() => setisSubmitting(false));
    }
  });

  const { values, setFieldValue, handleChange: formikhandleChange, touched, errors } = formik;
  // console.log(values);
  // console.log(errors);

  // حساب الإجماليات الحية للعرض (UX Improvement)
  const liveTotals = useMemo(() => {
    const lines = values.apExpenseLinesList || [];
    const amount = lines.reduce((sum: number, item: any) => sum + (Number(item.amount) || 0), 0);
    const vat = lines.reduce((sum: number, item: any) => sum + (Number(item.vat) || 0), 0);
    return { amount, vat, total: amount + vat };
  }, [values.apExpenseLinesList]);

  // منطق الربط الآلي (Supplier logic)
  useEffect(() => {
    if (isEdit || !values.peopleId) return;
    const emp: any = OptionPersonSuppliers?.find((f: any) => f.value === values.peopleId);
    if (emp) {
      if (values.personId !== emp.personId) setFieldValue('personId', emp.personId);
      if (values.currencyCode !== emp.invoiceCurrencyCode) setFieldValue('currencyCode', emp.invoiceCurrencyCode);
    }
  }, [values.peopleId, isEdit, OptionPersonSuppliers, setFieldValue, values.personId, values.currencyCode]);

  // دالة مساعدة لعرض رسائل الخطأ لـ React Select
  const renderError = (fieldName: string) => {
    const t = (touched as any)[fieldName];
    const e = (errors as any)[fieldName];
    return t && e ? <div className='text-danger small mt-1'>{e as string}</div> : null;
  };

  // console.log(errors);

  const {
    rate,
    loading: loadingRate,
    error: errorRate,
    refetch
  } = useDailyRate({
    currencyCode: values.currencyCode,
    date: values.expenseDate
  });
  const NotEditable = isViewOnly || !!dataById?.isConfirmed;
  if (loadingData) return <Loading loading={true} />;

  return (
    <div className='pb-5'>
      {/* الرأس (Header) */}
      {!isViewOnly && (
        <div className='d-flex align-items-center  '>
          <Breadcrumb
            BreadcrumbList={[
              { name: 'Home', link: ':moduleHome' },
              { name: t('المصروفات'), link: '/AP/ApExpense' },
              { name: isEdit ? t('تعديل') : t('جديد'), link: null }
            ]}
            PageName={
              <div className='d-flex align-items-center'>
                {isEdit ? t('تعديل تقرير مصروفات') : t('تقرير مصروفات جديد')}
                {values.expensType === 'FAST' && (
                  <Badge bg='warning' text='dark' className='ms-3 fs-12px'>
                    <i className='fas fa-bolt me-2'></i> {t(' سريــع')}
                  </Badge>
                )}
              </div>
            }
          />
          <div className='ms-auto'>
            <ButtonPrint PrintTemplateTypeId='34A7E1F1-2B8A-4249-A7DD-825D9F431E75' ReferenceId={dataById?.id || ''} />
          </div>
        </div>
      )}

      <Form onSubmit={formik.handleSubmit}>
        <Card className='shadow-sm border-0 mb-4'>
          <Loading loading={loading2 || loading4}>
            {NotEditable && (
              <div className='alert alert-primary d-flex align-items-center m-3 border-0 rounded-3'>
                <i className='fas fa-check-circle fa-2x me-3 text-primary'></i>

                <div>
                  <h6 className='mb-0 fw-bold'>{t('تم تأكيـد المصروفات')}</h6>
                  <small>{t('لا يمكن تعديل المصروفات بعد تأكيدها في النظام، السجل متاح للعرض فقط.')}</small>
                </div>
              </div>
            )}

            <CardBody>
              <Row>
                {/* التاريخ */}
                <Col lg={3} md={6}>
                  <div className='form-group mb-3'>
                    <label className='form-label small fw-bold text-muted'>
                      {t('التاريخ')} <span className='text-danger'>*</span>
                    </label>
                    <Form.Control
                      disabled={NotEditable}
                      type='date'
                      name='expenseDate'
                      onChange={formikhandleChange}
                      value={values.expenseDate || ''}
                      // className={formik.errors.expenseDate ? 'is-invalid' : ''}
                    />
                    {renderError('expenseDate')}
                  </div>
                </Col>

                {/* فئة المصروف */}
                <Col lg={3} md={6}>
                  <div className='form-group mb-3'>
                    <label className='form-label small fw-bold text-muted'>
                      {t('فئة المصروف')} <span className='text-danger'>*</span>
                    </label>
                    <Select
                      classNamePrefix='react-select'
                      isDisabled={NotEditable}
                      options={OptionsExpenseCategory}
                      value={OptionsExpenseCategory?.find((f: any) => f.value === values.expenseCategoryId)}
                      onChange={(opt: any) => setFieldValue('expenseCategoryId', opt?.value || '')}
                      placeholder={t('اختر الفئة...')}
                      className={formik.errors.expenseCategoryId ? 'is-invalid' : ''}
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base: any) => ({ ...base, zIndex: 9999 })
                      }}
                    />
                    {renderError('expenseCategoryId')}
                  </div>
                </Col>

                {/* المورد */}
                <Col lg={3} md={6}>
                  <div className='form-group mb-3'>
                    <label className='form-label small fw-bold text-muted'>
                      {t('المورد / الموظف')} <span className='text-danger'>*</span>
                    </label>
                    <Select
                      classNamePrefix='react-select'
                      isDisabled={NotEditable}
                      options={OptionPersonSuppliers}
                      value={OptionPersonSuppliers?.find((f: any) => f.value === values.peopleId)}
                      onChange={(opt: any) => setFieldValue('peopleId', opt?.value || '')}
                      placeholder={t('اختر...')}
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base: any) => ({ ...base, zIndex: 9999 })
                      }}
                    />
                    {renderError('peopleId')}
                  </div>
                </Col>

                {/* الوحدة التشغيلية */}
                <Col lg={3} md={6}>
                  <div className='form-group mb-3'>
                    <label className='form-label small fw-bold text-muted'>
                      {t('الوحدة التشغيلية')} <span className='text-danger'>*</span>
                    </label>
                    <Select
                      classNamePrefix='react-select'
                      isDisabled={NotEditable}
                      options={OperatingUnitOptions}
                      value={OperatingUnitOptions?.find((f: any) => f.value === values.organizationId)}
                      onChange={(opt: any) => setFieldValue('organizationId', opt?.value || '')}
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base: any) => ({ ...base, zIndex: 9999 })
                      }}
                    />
                    {renderError('organizationId')}
                  </div>
                </Col>

                {/* FAST MODE UI SECTION */}
                {values.expensType === 'FAST' && (
                  <Col md={12}>
                    <Card className='p-3 mb-3  shadow bg-theme bg-opacity-10  '>
                      <Row>
                        <Col md={6}>
                          <label className='form-label small fw-bold  '>{t('طريقة الدفع')}</label>
                          <Form.Select
                            name='paymentMethodId'
                            onChange={formik.handleChange}
                            value={values.paymentMethodId || ''}
                            // className='border-warning-subtl'
                          >
                            <option value=''>{t('اختر...')}</option>
                            {PaymentMethodList?.map((item: any) => (
                              <option key={item.id} value={item.id}>
                                {i18n.language === 'en' ? item.name : item.name2}
                              </option>
                            ))}
                          </Form.Select>
                        </Col>
                        <Col md={6}>
                          <label className='form-label small fw-bold '>{t('البنك / الصندوق')}</label>
                          <Select
                            classNamePrefix='react-select'
                            options={groupedOptions}
                            value={OptionsBankAccounts?.find((f: any) => f?.value === values.apBankAccountId)}
                            onChange={opt => setFieldValue('apBankAccountId', opt?.value || null)}
                            menuPortalTarget={document.body}
                            styles={{
                              menuPortal: (base: any) => ({ ...base, zIndex: 9999 })
                            }}
                          />
                        </Col>
                      </Row>
                    </Card>
                  </Col>
                )}

                {/* ('Currency') */}
                <Col lg={2} md={4}>
                  <div className='form-group mb-3'>
                    <label className='form-label'> {t('Currency')} </label>
                    <Form.Select
                      disabled={NotEditable}
                      className='form-select'
                      name='currencyCode'
                      onChange={formikhandleChange}
                      value={values.currencyCode || ''}
                      // isInvalid={!!(touched.currencyCode && errors.currencyCode)}
                    >
                      <option value=''>{i18n.language === 'ar' ? 'إختر' : 'Choose'}</option>

                      {Currency &&
                        Currency?.map((item: any, idx: number) => (
                          <option key={++idx} value={item.currencyCode}>
                            {item.description}
                          </option>
                        ))}
                    </Form.Select>
                    <div className='text-danger small'>{formik.touched.currencyCode && formik.errors.currencyCode}</div>
                  </div>
                </Col>
                {/* ('معدل العملة') */}
                <Col lg={2} md={4}>
                  <div className='form-group mb-3'>
                    <label className='form-label'> {t('CurrencyConversionRate')} </label>
                    <InputGroup>
                      <Form.Control
                        className='form-control text-center'
                        type='text'
                        readOnly
                        value={loadingRate ? '' : errorRate ? 'فشل التحميل' : rate}
                        placeholder={loadingRate ? 'جاري التحميل...' : ''}
                      />

                      {loadingRate && (
                        <InputGroup.Text>
                          <div className='spinner-border spinner-border-sm text-primary'></div>
                        </InputGroup.Text>
                      )}

                      {errorRate && !loadingRate && (
                        <InputGroup.Text style={{ padding: '0' }}>
                          <button
                            type='button'
                            className='btn btn-outline-warning h-100 px-2'
                            onClick={refetch}
                            title='إعادة تحميل سعر الصرف'
                          >
                            <i className='fas fa-redo'></i>
                          </button>
                        </InputGroup.Text>
                      )}
                    </InputGroup>

                    {errorRate && !loadingRate && (
                      <small className='text-warning mt-1 d-block text-center'>
                        <i className='fas fa-exclamation-circle me-1'></i>
                        تعذر تحميل السعر
                        <button type='button' className='btn btn-link btn-sm p-0 text-warning ms-1' onClick={refetch}>
                          (إعادة المحاولة)
                        </button>
                      </small>
                    )}
                  </div>
                </Col>

                <Col lg={8} md={6}>
                  <div className='form-group mb-3'>
                    <label className='form-label small fw-bold text-muted'>{t('البيان العام')}</label>
                    <Form.Control
                      disabled={NotEditable}
                      name='description'
                      placeholder={t('اكتب وصفاً مختصراً للتقرير...')}
                      onChange={formik.handleChange}
                      value={values.description}
                    />
                    {renderError('description')}
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Loading>
        </Card>

        {/* تفاصيل السطور */}
        {values.expenseCategoryId ? (
          <Card className='shadow-sm border-0  '>
            <ApExpenseLines formik={formik} NotEditable={NotEditable || false} />
            {Array.isArray(formik.errors.apExpenseLinesList) && (
              <div className='bg-danger-subtle p-2 text-danger text-center small fw-bold'>
                {' '}
                {t('يرجى التحقق من صحة بيانات السطور أعلاه')}{' '}
              </div>
            )}
          </Card>
        ) : (
          <Card>
            <div className='text-center p-5  shadow-sm rounded-3 border-2 border-dashed border-light'>
              <div className='mb-3'>
                <i className='fas fa-hand-pointer fa-3x text-theme opacity-45'></i>
              </div>
              <p className='text-muted fw-bold'>{t('يرجى اختيار "فئة المصروف" من الأعلى للبدء في إضافة البنود')}</p>
            </div>
          </Card>
        )}

        {/* أزرار الأكشن (Footer Actions) */}
        {!isViewOnly && (
          <Card className='mt-3 p-3  shadow'>
           

            <div className='d-flex justify-content-center gap-3 sticky-bottom'>
  <button
    type='button'
    onClick={() => navigate('/AP/ApExpense', { replace: true })}
    className='btn btn-outline-secondary px-4'
  >
    <i className='fas fa-times me-2'></i> {t('Cancel')}
  </button>

  <div className='btn-group'>
    {/* ✅ الزر الظاهر: مسودة */}
    <button
      type='button'
      onClick={async () => {
        await formik.setFieldValue('isConfirmed', false);
        await formik.submitForm();
      }}
      disabled={isSubmitting || NotEditable}
      className='btn btn-theme px-4'
    >
      {isSubmitting ? (
        <span className='spinner-border spinner-border-sm me-2'></span>
      ) : (
        <i className='fas fa-file-edit me-2'></i>
      )}
      {t('حفظ كمسودة')}
    </button>

    {/* ✅ سهم الدروبداون */}
    <button
      type='button'
      className='btn btn-outline-theme dropdown-toggle dropdown-toggle-split'
      data-bs-toggle='dropdown'
      aria-expanded='false'
      disabled={isSubmitting || NotEditable}
    >
      <span className='visually-hidden'>Toggle Dropdown</span>
    </button>

    {/* ✅ القائمة */}
    <ul className='dropdown-menu dropdown-menu-end'>
 
      <li>
        <button
          type='button'
          className='dropdown-item fw-bold'
          disabled={isSubmitting || NotEditable}
          onClick={async () => {
            await formik.setFieldValue('isConfirmed', true);
            await formik.submitForm();
          }}
        >
          <i className='fas fa-check-circle me-2 text-theme'></i>
          {t('حفظ وتأكيد نهائي')}
        </button>
      </li>
    </ul>
  </div>
</div>

          </Card>
        )}

        
      </Form>
    </div>
  );
};

export default ApExpenseForm;
